import React, { useMemo } from 'react';
import { DataTable, Column } from '@/components/ui/data-table';
import { StatusBadge, getStatusAccentColor, MetadataText } from '@/components/ui/table-components';
import { JobMatches } from '../../interfaces';
import { formatMatchStatusLabel } from '@/utils/matchStatus';

interface RoleMatchesTableProps {
  matches: JobMatches[];
  onMatchClick: (match: JobMatches) => void;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (columnId: string) => void;
  isLoading?: boolean;
}

export function RoleMatchesTable({
  matches,
  onMatchClick,
  sortBy,
  sortDirection,
  onSort,
  isLoading = false,
}: RoleMatchesTableProps) {
  const columns: Column<JobMatches>[] = useMemo(
    () => [
      {
        id: 'clientName',
        header: 'Client',
        sortable: true,
        cell: (match) => (
          <div>
            <div className="font-medium text-slate-900 dark:text-slate-100">
              {match.clientName || '-'}
            </div>
          </div>
        ),
      },
      {
        id: 'jobTitle',
        header: 'Role',
        sortable: true,
        cell: (match) => (
          <div className="font-medium text-slate-900 dark:text-slate-100">
            {match.jobTitle || '-'}
          </div>
        ),
      },
      {
        id: 'jobLocation',
        header: 'Role Location',
        sortable: true,
        cell: (match) => (
          <span className="text-slate-700 dark:text-slate-300">
            {match.jobLocation?.displayName || '-'}
          </span>
        ),
      },
      {
        id: 'candidateJobTitle',
        header: 'Candidate',
        sortable: true,
        cell: (match) => (
          <div>
            <div className="font-medium text-slate-900 dark:text-slate-100">
              {match.candidateJobTitle || '-'}
            </div>
            {match.candidateIdealJobTitles && (
              <MetadataText className="mt-0.5">
                {match.candidateIdealJobTitles}
              </MetadataText>
            )}
          </div>
        ),
      },
      {
        id: 'candidateLocation',
        header: 'Candidate Location',
        sortable: true,
        cell: (match) => (
          <span className="text-slate-700 dark:text-slate-300">
            {match.candidateLocation?.displayName || '-'}
          </span>
        ),
      },
      {
        id: 'currentStatus',
        header: 'Status',
        sortable: true,
        className: 'text-center',
        cell: (match) => (
          <StatusBadge status={formatMatchStatusLabel(match.currentStatus || 'UNKNOWN')} />
        ),
      },
    ],
    []
  );

  const getRowId = (match: JobMatches, index: number) => {
    if (match?.id) {
      return `job-match-${match.id}`;
    }
    return `job-match-idx-${index}`;
  };

  return (
    <DataTable
      data={matches}
      columns={columns}
      onRowClick={onMatchClick}
      getRowId={getRowId}
      getRowAccentColor={(match) => getStatusAccentColor(match.currentStatus)}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSort={onSort}
      isLoading={isLoading}
      emptyMessage="No role matches yet."
    />
  );
}
